import React from 'react';
import { useDraggable } from '@dnd-kit/core';

const SECTION_TYPES = [
  { type: 'title-page', label: 'Title Page', icon: '📄', desc: 'Report cover page' },
  { type: 'table-of-contents', label: 'Table of Contents', icon: '📑', desc: 'Auto-generated TOC' },
  { type: 'rich-text', label: 'Rich Text', icon: '📝', desc: 'Formatted text section' },
  { type: 'severity-dashboard', label: 'Dashboard', icon: '📊', desc: 'Finding severity counts' },
  { type: 'risk-rating-table', label: 'Risk Rating', icon: '⚖️', desc: 'CVSS rating scale' },
  { type: 'scope-table', label: 'Scope', icon: '🎯', desc: 'In-scope items table' },
  { type: 'findings-summary', label: 'Findings Summary', icon: '📋', desc: 'Vulnerability summary table' },
  { type: 'findings-detail', label: 'Findings Detail', icon: '🔍', desc: 'Per-finding breakdown' },
  { type: 'page-break', label: 'Page Break', icon: '➖', desc: 'Force new page' },
  { type: '_separator', label: 'Components' },
  { type: 'heading', label: 'Heading', icon: 'H', desc: 'Section heading' },
  { type: 'text-block', label: 'Text Block', icon: '¶', desc: 'Rich text content' },
  { type: 'custom-table', label: 'Custom Table', icon: '▦', desc: 'User-defined table' },
  { type: 'image', label: 'Image / Figure', icon: '🖼️', desc: 'Upload image' },
  { type: 'severity-chart', label: 'Severity Chart', icon: '🥧', desc: 'Pie or bar chart' },
  { type: 'horizontal-line', label: 'Horizontal Line', icon: '─', desc: 'Visual separator line' },
  { type: 'spacer', label: 'White Space', icon: '↕', desc: 'Blank vertical gap' },
  { type: 'version-history', label: 'Version History', icon: '🕑', desc: 'Versions, dates and who generated them' },
  { type: 'metric-tiles', label: 'Metric Tiles', icon: '▤', desc: 'A row of captioned figures' },
  { type: 'findings-walkthrough', label: 'Findings Walkthrough', icon: '🧾', desc: 'Findings table with a line of description on each row' },
  { type: 'revalidation-summary', label: 'Revalidation Summary', icon: '🔁', desc: 'Retested findings with their original risk and before/after status' },
  // A real section type, not a preset like the two below: its rows are the
  // engagement's own findings grouped by category, and a preset can only
  // carry literal text.
  { type: 'owasp-findings', label: 'TSR OWASP Findings', icon: '🛡️', desc: 'Findings grouped by OWASP Top 10 category' },
  { type: 'finding-cards', label: 'TSR Findings Walkthrough Section', icon: '🗂️', desc: 'A card per finding — title bar, facts grid and prose sections' },
  // A prefilled Custom Section, not a type of its own, so every band stays
  // editable. Hidden from client users by the caller's `hidden` list.
  { type: 'tsr-dashboard', label: 'TSR Dashboard', icon: '📐', desc: 'House opening page — editable once added' },
  { type: 'tsr-findings-walkthrough', label: 'TSR Findings Walkthrough', icon: '🧾', desc: 'House findings page with next steps — editable once added' },
  { type: '_separator2', label: 'Advanced' },
  { type: 'composite', label: 'Custom Section', icon: '🧩', desc: 'Composable sub-components' },
  { type: '_separator3', label: 'Page furniture' },
  { type: 'header', label: 'Header', icon: '⬆️', desc: 'Repeats at the top of every page' },
  { type: 'footer', label: 'Footer', icon: '⬇️', desc: 'Repeats at the bottom of every page' },
];

// Header and footer are not part of the document flow — they repeat on every
// page — so they are added by click only. Dragging one to a position between
// two sections would imply an ordering that does not exist.
const NOT_DRAGGABLE = new Set(['header', 'footer']);

export { SECTION_TYPES };

function PaletteButton({ dragId, dragData, draggable, icon, label, desc, onClick, title }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: dragId,
    data: dragData,
    disabled: !draggable,
  });

  return (
    <button
      ref={draggable ? setNodeRef : undefined}
      {...(draggable ? listeners : {})}
      {...(draggable ? attributes : {})}
      onClick={onClick}
      title={title}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
        background: 'var(--bg-input)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius)', cursor: !draggable ? 'pointer' : isDragging ? 'grabbing' : 'grab', textAlign: 'left',
        transition: 'all 0.15s', color: 'var(--text)', width: '100%',
        opacity: isDragging ? 0.5 : 1,
      }}
      onMouseOver={e => { if (!isDragging) e.currentTarget.style.borderColor = 'var(--primary)'; }}
      onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border)'}
    >
      <span style={{ fontSize: 16, lineHeight: 1, flexShrink: 0 }}>{icon}</span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
        <div style={{ fontSize: 10, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{desc}</div>
      </div>
    </button>
  );
}

function DraggablePaletteItem({ section, onAddSection }) {
  const draggable = !NOT_DRAGGABLE.has(section.type);
  return (
    <PaletteButton
      dragId={`palette-${section.type}`}
      dragData={{ fromPalette: true, type: section.type, label: section.label }}
      draggable={draggable}
      icon={section.icon}
      label={section.label}
      desc={section.desc}
      onClick={() => onAddSection(section.type, section.label)}
    />
  );
}

const GROUP_HEADING = {
  fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
  color: 'var(--text-secondary)', padding: '10px 0 4px', marginTop: 4,
  borderTop: '1px solid var(--border)',
};

/** What a saved entry says underneath its name when its author wrote nothing. */
const SAVED_DESC = {
  composite: 'Saved custom section',
  'rich-text': 'Saved text section',
  heading: 'Saved heading',
  'text-block': 'Saved text block',
  'custom-table': 'Saved table',
};

/**
 * A saved section, as a palette entry.
 *
 * The delete sits beside the button rather than inside it: the entry is itself
 * a <button>, and a button inside a button is invalid markup that React will
 * render and browsers will treat inconsistently.
 */
function SavedSectionItem({ saved, onAddSaved, onDeleteSaved }) {
  return (
    <div style={{ display: 'flex', alignItems: 'stretch', gap: 4 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <PaletteButton
          dragId={`palette-saved-${saved.id}`}
          dragData={{ fromPalette: true, savedId: saved.id, label: saved.name }}
          draggable
          icon="⭐"
          label={saved.name}
          desc={saved.description || SAVED_DESC[saved.section_json?.type] || 'Saved section'}
          onClick={() => onAddSaved(saved)}
          title={saved.created_by_name ? `Saved by ${saved.created_by_name}` : undefined}
        />
      </div>
      {saved.canEdit && onDeleteSaved && (
        <button
          onClick={() => onDeleteSaved(saved)}
          title={`Remove "${saved.name}" from Customer Templates`}
          style={{
            background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
            cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 13, padding: '0 7px',
            flexShrink: 0, fontFamily: 'inherit',
          }}
          onMouseOver={e => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.borderColor = '#ef4444'; }}
          onMouseOut={e => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
        >✕</button>
      )}
    </div>
  );
}

export default function SectionPalette({
  onAddSection, hidden = [], savedSections = [], onAddSaved, onDeleteSaved, savedError,
}) {
  const visible = SECTION_TYPES.filter(s => !hidden.includes(s.type));
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', marginBottom: 8 }}>
        Sections
      </div>
      <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginBottom: 8, lineHeight: 1.4 }}>
        Click to add at bottom, or drag onto the canvas to insert at a position.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {visible.map((s, i) => {
          if (s.type.startsWith('_separator')) {
            // A group heading with nothing left under it is just a stray rule.
            const next = visible[i + 1];
            if (!next || next.type.startsWith('_separator')) return null;
            return <div key={s.type} style={GROUP_HEADING}>{s.label}</div>;
          }
          return <DraggablePaletteItem key={s.type} section={s} onAddSection={onAddSection} />;
        })}

        {/* Customer Templates — sections this organisation has saved. The
            heading shows even when the shelf is empty, because the way to put
            something on it is a button on a section the author is already
            looking at, and nothing else would tell them it is there. */}
        {onAddSaved && (
          <>
            <div style={GROUP_HEADING}>Customer Templates</div>
            {/* Above the list, never instead of it. A failed delete used to
                replace every entry with its error message, so one 404 emptied
                the author's whole shelf until they reloaded the page. */}
            {savedError && (
              <div style={{ fontSize: 10, color: '#ef4444', lineHeight: 1.4, padding: '2px 0' }}>
                {savedError}
              </div>
            )}
            {savedSections.length === 0 ? (
              // "None yet" under a message saying the list could not be loaded
              // would be telling the author something nobody knows.
              savedError ? null : (
                <div style={{ fontSize: 10, color: 'var(--text-secondary)', lineHeight: 1.4, padding: '2px 0' }}>
                  None yet. Build a section on the canvas, then use <strong>★ Save</strong> on it to
                  keep it here for everyone in your organisation.
                </div>
              )
            ) : (
              savedSections.map(saved => (
                <SavedSectionItem key={saved.id} saved={saved}
                  onAddSaved={onAddSaved} onDeleteSaved={onDeleteSaved} />
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
}
